/**
 * OrangeHRM Calendar Plugin — Calendar Utilities
 * Ported from Syncova (recurrence, teams, tags removed).
 */

export interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  dateKey: string; // 'YYYY-MM-DD'
}

/**
 * Returns a 7-col grid of days for a given month view (Sunday-anchored).
 * Fills in days from prev/next month to complete the grid rows.
 */
export function getCalendarDays(year: number, month: number): CalendarDay[] {
  const today = new Date();
  const todayKey = formatDateKey(today);

  const firstOfMonth = new Date(year, month - 1, 1);
  const lastOfMonth = new Date(year, month, 0);

  // Start from the Sunday on or before the 1st
  const startDay = new Date(firstOfMonth);
  startDay.setDate(startDay.getDate() - startDay.getDay());

  // End on the Saturday on or after the last day
  const endDay = new Date(lastOfMonth);
  const daysToAdd = 6 - endDay.getDay();
  endDay.setDate(endDay.getDate() + daysToAdd);

  const days: CalendarDay[] = [];
  const cursor = new Date(startDay);

  while (cursor <= endDay) {
    const dateKey = formatDateKey(cursor);
    days.push({
      date: new Date(cursor),
      isCurrentMonth: cursor.getMonth() === month - 1,
      isToday: dateKey === todayKey,
      dateKey,
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
}

/**
 * Groups events (with an `eventDatetime` string) by their date key ('YYYY-MM-DD').
 */
export function groupEventsByDay<T extends {eventDatetime: string}>(
  events: T[],
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const event of events) {
    const key = event.eventDatetime.slice(0, 10);
    const list = map.get(key);
    if (list) {
      list.push(event);
    } else {
      map.set(key, [event]);
    }
  }
  return map;
}

/**
 * Groups holidays (with a `date` field 'YYYY-MM-DD') by their date key.
 */
export function groupHolidaysByDay<T extends {date: string}>(
  holidays: T[],
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const h of holidays) {
    const key = h.date.slice(0, 10);
    const list = map.get(key);
    if (list) {
      list.push(h);
    } else {
      map.set(key, [h]);
    }
  }
  return map;
}

/**
 * Format a Date to 'YYYY-MM-DD'.
 */
export function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Format a Date to 'YYYY-MM-DDTHH:mm' for datetime-local inputs.
 */
export function formatDatetimeLocal(date: Date): string {
  const key = formatDateKey(date);
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${key}T${h}:${min}`;
}

/**
 * Compute minutes worked from a punch-in/out pair.
 */
export function computeWorkedMinutes(
  punchIn: string,
  punchOut: string | null,
): number {
  if (!punchOut) return 0;
  const inMs = new Date(punchIn).getTime();
  const outMs = new Date(punchOut).getTime();
  return Math.max(0, Math.floor((outMs - inMs) / 60000));
}

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface WorkWeekConfig {
  mon?: number;
  tue?: number;
  wed?: number;
  thu?: number;
  fri?: number;
  sat?: number;
  sun?: number;
  [key: string]: number | undefined;
}

const DOW_KEYS: Array<keyof WorkWeekConfig> = [
  'sun',
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
];

/**
 * Checks if a given date is the 3rd Saturday of its month.
 * Any Saturday (day 6) between day 15 and 21 is mathematically the 3rd Saturday.
 */
export function isThirdSaturday(date: Date): boolean {
  if (date.getDay() !== 6) return false;
  const dayOfMonth = date.getDate();
  return dayOfMonth >= 15 && dayOfMonth <= 21;
}

/**
 * Checks if a date is a non-working day based on workweek config (length === 8)
 * or company 3rd Saturday policy.
 */
export function isNonWorkingDay(
  date: Date,
  workweek?: WorkWeekConfig | null,
  includeThirdSaturday = true,
): boolean {
  if (includeThirdSaturday && isThirdSaturday(date)) {
    return true;
  }
  const dow = DOW_KEYS[date.getDay()];
  if (workweek && typeof workweek[dow] === 'number') {
    return workweek[dow] === 8;
  }
  // Default fallback if no workweek: Sunday is off
  return date.getDay() === 0;
}

/**
 * Generates CSV lines for all 3rd Saturdays of a given year.
 */
export function generateThirdSaturdaysCSV(year: number): string {
  const lines: string[] = [];
  for (let m = 0; m < 12; m++) {
    for (let d = 15; d <= 21; d++) {
      const dt = new Date(year, m, d);
      if (dt.getDay() === 6) {
        lines.push(`${formatDateKey(dt)}, 3rd Saturday Off, Full Day, No`);
        break;
      }
    }
  }
  return lines.join('\n');
}

